import { z } from 'zod';
import { LOGIN_PATTERN } from './employees';
import { SCOPES, USER_PERMISSION_EFFECTS } from './permissions';
import { QUEUE_FUNCTIONS } from './process';

// Komunikaty walidacji po polsku (dotyczy wszystkich schematów zod w procesie).
z.config(z.locales.pl());

export const loginSchema = z.object({
  /** login albo e-mail konta (nazwa pola zostaje „email” — zgodność ze starszymi aplikacjami) */
  email: z.string().trim().toLowerCase().min(1, 'Podaj login').max(200),
  password: z.string().min(1, 'Podaj hasło').max(200),
  /** kod z aplikacji uwierzytelniającej albo kod zapasowy — gdy konto ma logowanie dwuetapowe */
  code: z.string().trim().max(20).optional(),
  /** „Zapamiętaj mnie”: długa sesja (trwałe ciasteczko) zamiast sesji do zamknięcia przeglądarki */
  remember: z.boolean().optional(),
});

export const mfaCodeSchema = z.object({ code: z.string().trim().min(6, 'Podaj 6-cyfrowy kod').max(20) });
export const mfaDisableSchema = z.object({ password: z.string().min(1, 'Podaj hasło').max(200), code: z.string().trim().min(6).max(20) });
export type LoginInput = z.infer<typeof loginSchema>;

const nip = z
  .string()
  .trim()
  .transform((s) => s.replace(/[\s-]/g, ''))
  .refine((s) => s === '' || /^\d{10}$/.test(s), 'NIP musi mieć 10 cyfr');

export const companyUpdateSchema = z.object({
  fullName: z.string().trim().min(3).max(200),
  shortName: z.string().trim().min(2).max(60),
  nip,
  regon: z.string().trim().max(14),
  krs: z.string().trim().max(10),
  street: z.string().trim().max(120),
  postalCode: z.string().trim().max(10),
  city: z.string().trim().max(80),
  bankAccount: z.string().trim().max(40),
  email: z.union([z.literal(''), z.string().trim().email('Niepoprawny e-mail')]),
  phone: z.string().trim().max(30),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Kolor w formacie #RRGGBB'),
  settings: z.object({
    offerValidityDays: z.number().int().min(1).max(365),
    maxDiscountPct: z.number().min(0).max(100),
    minMarginPct: z.number().min(0).max(100),
    defaultDepositPct: z.number().min(0).max(100),
    acceptanceOtp: z.boolean().default(true),
    offerTerms: z.string().trim().max(6000).default(''),
    depositDocument: z.enum(['brak', 'proforma', 'zaliczkowa', 'koncowa']).default('zaliczkowa'),
    finalDocument: z.enum(['brak', 'proforma', 'zaliczkowa', 'koncowa']).default('koncowa'),
    eventPaymentDays: z.number().int().min(0, 'Liczba dni nie może być ujemna').max(120, 'Najwyżej 120 dni').default(7),
    paymentReminderDays: z.number().int().min(0, 'Liczba dni nie może być ujemna').max(60, 'Najwyżej 60 dni').default(0),
    contractThresholdGr: z.number().int().min(0).max(100_000_000_00).default(500000),
    completionNotice: z.boolean().default(false),
    scheduleEmailScan: z.boolean().default(false),
    offerExpiryNotice: z.boolean().default(false),
    clientPortal: z.boolean().default(true),
  }),
});
export type CompanyUpdateInput = z.infer<typeof companyUpdateSchema>;

/** Nowa firma: skrót (1–4 znaki) jest stały — trafia do numerów dokumentów. */
export const companyCreateSchema = companyUpdateSchema.extend({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{1,4}$/, 'Skrót: od 1 do 4 wielkich liter lub cyfr'),
});
export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;

export const seriesUpdateSchema = z.object({
  pattern: z.string().trim().min(3).max(60),
  reason: z.string().trim().min(3, 'Podaj powód zmiany').max(300),
});

const password = z.string().min(10, 'Hasło musi mieć co najmniej 10 znaków').max(200);

/** e-mail kontaktowy: pusty = brak */
const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Niepoprawny adres e-mail')
  .transform((v) => (v === '' ? null : v));
const login = z.string().trim().toLowerCase().regex(LOGIN_PATTERN, 'Login: 3–40 znaków — małe litery, cyfry, kropka, podkreślenie lub myślnik');

/**
 * Nowy pracownik. Konto (logowanie do CRM i aplikacji) jest opcjonalne: bez konta — bez loginu, hasła i ról.
 * Login pusty — system tworzy go z imienia i nazwiska (Łukasz Bek → lbek).
 */
export const userCreateSchema = z
  .object({
    email: optionalEmail.default(null),
    fullName: z.string().trim().min(3).max(120),
    phone: z.string().trim().max(30).default(''),
    hasAccount: z.boolean().default(true),
    login: login.optional(),
    password: password.optional(),
    isSubcontractor: z.boolean().default(false),
    roleCodes: z.array(z.string()).default([]),
    /** wszystkie firmy (także dodane później) — domyślnie; wtedy companyIds nie ma znaczenia */
    allCompanies: z.boolean().default(true),
    companyIds: z.array(z.string().uuid()).default([]),
    /** hasło początkowe do zmiany przy pierwszym logowaniu */
    mustChangePassword: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (!v.hasAccount) return;
    if (!v.password) ctx.addIssue({ code: 'custom', path: ['password'], message: 'Ustaw hasło konta' });
    if (!v.roleCodes.length) ctx.addIssue({ code: 'custom', path: ['roleCodes'], message: 'Wybierz co najmniej jedną rolę' });
  });
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z
  .object({
    /** brak = bez zmian; pusty = usuń e-mail */
    email: optionalEmail.optional(),
    fullName: z.string().trim().min(3).max(120),
    phone: z.string().trim().max(30),
    /** brak = bez zmian */
    hasAccount: z.boolean().optional(),
    /** brak = bez zmian (przy włączeniu konta bez loginu — z imienia i nazwiska) */
    login: login.optional(),
    /** hasło przy włączaniu konta, które go jeszcze nie ma */
    password: password.optional(),
    isSubcontractor: z.boolean(),
    roleCodes: z.array(z.string()),
    /** brak = bez zmian */
    allCompanies: z.boolean().optional(),
    companyIds: z.array(z.string().uuid()),
    active: z.boolean(),
  });
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const passwordResetSchema = z.object({ password, mustChangePassword: z.boolean().default(false) });

/** Wymagana zmiana hasła przy kolejnym logowaniu (karta pracownika → Hasło). */
export const passwordChangeRequiredSchema = z.object({ required: z.boolean() });
/** Kod błędu 403: konto ma wymaganą zmianę hasła — działa tylko zmiana hasła, profil i wylogowanie. */
export const PASSWORD_CHANGE_REQUIRED = 'WYMAGANA_ZMIANA_HASLA';

/** „Nie pamiętasz hasła?” — login albo e-mail konta; odpowiedź zawsze ta sama (nie zdradza, czy konto istnieje). */
export const passwordResetRequestSchema = z.object({ login: z.string().trim().toLowerCase().min(1, 'Podaj login albo e-mail').max(200) });
/** Nowe hasło z linku w e-mailu. */
export const passwordResetConfirmSchema = z.object({ token: z.string().trim().min(20).max(200), newPassword: password });

/** Samodzielna zmiana własnego hasła: obecne hasło + nowe (te same zasady siły co przy ustawianiu przez administratora). */
export const passwordChangeSchema = z
  .object({ currentPassword: z.string().min(1, 'Podaj obecne hasło').max(200), newPassword: password })
  .refine((v) => v.currentPassword !== v.newPassword, { message: 'Nowe hasło musi się różnić od obecnego', path: ['newPassword'] });
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;

// ───────────── role (edytor uprawnień) ─────────────

export const roleCreateSchema = z.object({
  name: z.string().trim().min(2, 'Podaj nazwę roli').max(60),
});
export type RoleCreateInput = z.infer<typeof roleCreateSchema>;

export const roleRenameSchema = z.object({ name: z.string().trim().min(2, 'Podaj nazwę roli').max(60) });

export const rolePermissionsSchema = z.object({
  permissions: z.array(z.object({ permission: z.string().max(60), scope: z.enum(SCOPES) })).max(500),
  /** kolejki zadań w sprawach (QUEUE_FUNCTIONS); brak pola — bez zmian */
  queues: z.array(z.enum(QUEUE_FUNCTIONS)).max(10).optional(),
});

/** Branże pracy w terenie (funkcja „monter”): wszystkie albo wybrane; pusta lista przy all=false — brak. */
export const userWorkTradesSchema = z.object({ all: z.boolean(), codes: z.array(z.string().trim().min(1).max(4)).max(30) });

/** Prawa indywidualne pracownika — pełna lista (zastępuje poprzednią). */
export const userPermissionsSchema = z.object({
  entries: z
    .array(z.object({ permission: z.string().max(60), scope: z.enum(SCOPES), effect: z.enum(USER_PERMISSION_EFFECTS) }))
    .max(300),
});
export type UserPermissionsInput = z.infer<typeof userPermissionsSchema>;

/** Odpowiedź przeglądarki WebAuthn (rejestracja lub logowanie kluczem dostępu) — weryfikuje ją serwer. */
export const passkeyFinishSchema = z.object({
  challengeId: z.string().uuid(),
  response: z.record(z.string(), z.unknown()),
  name: z.string().trim().max(80).optional(),
});
/** Logowanie kluczem dostępu — jak wyżej, z wyborem „Zapamiętaj mnie”. */
export const passkeyLoginSchema = passkeyFinishSchema.extend({ remember: z.boolean().optional() });
export type PasskeyLoginInput = z.infer<typeof passkeyLoginSchema>;
export const passkeyRenameSchema = z.object({ name: z.string().trim().min(1, 'Podaj nazwę').max(80) });

